import { Group, Text, ActionIcon, Menu, Box } from '@mantine/core';
import {
  MoreVertical,
  Copy,
  Archive,
  RotateCcw,
  Trash2,
  Circle,
} from 'react-feather';
import type { TaskTemplateWithDetails } from '../../../../types';
import { getAnchorConfig } from '../../../../config/taskConfig';

interface TemplateRowProps {
  template: TaskTemplateWithDetails;
  subtasks?: TaskTemplateWithDetails[];
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onEdit: (template: TaskTemplateWithDetails) => void;
  onDuplicate: (template: TaskTemplateWithDetails) => void;
  onArchive: (template: TaskTemplateWithDetails) => void;
  onRestore: (template: TaskTemplateWithDetails) => void;
  onDelete: (template: TaskTemplateWithDetails) => void;
  isSubtask?: boolean;
}

export function TemplateRow({
  template,
  subtasks = [],
  onEdit,
  onDuplicate,
  onArchive,
  onRestore,
  onDelete,
  isSubtask = false,
}: TemplateRowProps) {
  const anchorConfig = getAnchorConfig(template.anchor_type);
  const hasSubtasks = subtasks.length > 0 || template.subtask_count > 0;

  // Format the due date rule
  const formatDueDateRule = () => {
    let rule = anchorConfig.label;
    if (template.anchor_session_number) {
      rule += ` #${template.anchor_session_number}`;
    }
    if (template.days_offset !== 0) {
      rule += ` ${template.days_offset > 0 ? '+' : ''}${template.days_offset} days`;
    }
    return rule;
  };

  // Build metadata text for line 2
  const buildMetadataText = () => {
    const parts: string[] = [];
    if (template.job_title_name) {
      parts.push(template.job_title_name);
    }
    parts.push(formatDueDateRule());
    if (hasSubtasks && !isSubtask) {
      parts.push(`${template.subtask_count || subtasks.length} subtask${(template.subtask_count || subtasks.length) !== 1 ? 's' : ''}`);
    }
    return parts.join('    ');
  };

  return (
    <>
      <Box
        py="sm"
        px="sm"
        style={{
          marginLeft: isSubtask ? '32px' : 0,
          opacity: template.is_archived ? 0.6 : 1,
          cursor: 'pointer',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
        }}
        onClick={() => onEdit(template)}
      >
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }} align="flex-start">
            {/* Circle checkbox for subtasks */}
            {isSubtask && (
              <Box pt={2}>
                <Circle size={18} color="var(--mantine-color-gray-4)" />
              </Box>
            )}

            {/* Two-line content */}
            <Box style={{ flex: 1, minWidth: 0 }}>
              {/* Line 1: Task title (bold) */}
              <Text
                size="sm"
                fw={600}
                style={{
                  textDecoration: template.is_archived ? 'line-through' : 'none',
                }}
              >
                {template.task_title}
              </Text>

              {/* Line 2: Metadata (muted) */}
              <Text size="xs" c="dimmed" mt={2}>
                {buildMetadataText()}
              </Text>
            </Box>
          </Group>

          {/* Actions menu */}
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<Copy size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(template);
                }}
              >
                Duplicate
              </Menu.Item>
              <Menu.Divider />
              {template.is_archived ? (
                <Menu.Item
                  leftSection={<RotateCcw size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore(template);
                  }}
                >
                  Restore
                </Menu.Item>
              ) : (
                <Menu.Item
                  leftSection={<Archive size={14} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive(template);
                  }}
                >
                  Archive
                </Menu.Item>
              )}
              <Menu.Item
                color="red"
                leftSection={<Trash2 size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(template);
                }}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Box>

      {/* Render subtasks - always expanded */}
      {subtasks.map(subtask => (
        <TemplateRow
          key={subtask.id}
          template={subtask}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onArchive={onArchive}
          onRestore={onRestore}
          onDelete={onDelete}
          isSubtask
        />
      ))}
    </>
  );
}

export default TemplateRow;
